import { z, RefinementCtx, number } from 'zod';
import {
  STEP_PHQ_1_ERRORS,
  PLEASE_SELECT_LEAST,
} from 'src/features/onboarding/components/6-health-question-form/constants';
import { t } from '@/i18n/index';
import { validateSSN } from '@utils/ssn';

const medicationSchema = z
  .object({
    id: z.string().min(1, { message: t('errors.medicationIdRequired') }),
    dosageId: z.string().optional(),
    dosageDescription: z.string().optional(),
    frequency: z.string().min(1, { message: t('errors.frequencyRequired') }),
    frequencyDescription: z.string().optional(),
    applicant: z.string().min(1, { message: t('errors.recipientRequired') }),
    description: z.string().optional(),
  })
  .superRefine((medication, ctx) => {
    // dosageDescription is required if dosageId is not provided
    // Check for empty string or falsy value
    const hasDosageDescription = medication.dosageDescription && medication.dosageDescription.trim().length > 0;
    if (!medication.dosageId && !hasDosageDescription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dosageDescription'],
        message: t('errors.dosageRequired'),
      });
    }
    
    if (!medication.frequency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['frequency'],
        message: t('errors.frequencyRequired'),
      });
    }
    
    // frequencyDescription is required for "Every X hours" or "As needed"
    // Check for empty string or falsy value
    const hasFrequencyDescription = medication.frequencyDescription && medication.frequencyDescription.trim().length > 0;
    if (
      (medication.frequency === 'Every X hours' || medication.frequency === 'As needed') &&
      !hasFrequencyDescription
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['frequencyDescription'],
        message: t('errors.frequencyDescriptionRequired'),
      });
    }
    
    if (!medication.applicant) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['applicant'],
        message: t('errors.recipientRequired'),
      });
    }
  });

export const onboardingSchema = z.object({
  householdIncome: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined) {
      return undefined;
    }
    return val;
  }, z.string({ required_error: t('errors.incomeRequired') })),
  applicants: z.array(
    z
      .object({
        applicant: z.enum(['spouse', 'dependent', 'primary']),
        firstName: z
          .string()
          .min(1, { message: t('errors.firstNameRequired') })
          .refine((val) => {
            const trimmed = val.trim();
            if (trimmed.length === 0) return false;
            if (!/[a-zA-Zа-яА-Яéñüóíáúèàùëïöäüÿçñ]/i.test(trimmed)) return false;
            if (/[0-9@#$%^&*()_+=<>{}[\]|\\/`~]/.test(trimmed)) return false;
            if (/<[^>]*>|script|javascript/i.test(trimmed)) return false;
            return true;
          }, {
            message: t('errors.firstNameInvalid'),
          }),
        midName: z.string().optional().refine((val) => {
            if (!val || val.trim().length === 0) return true;
            const trimmed = val.trim();
            if (!/[a-zA-Zа-яА-Яéñüóíáúèàùëïöäüÿçñ]/i.test(trimmed)) return false;
            if (/[0-9@#$%^&*()_+=<>{}[\]|\\/`~]/.test(trimmed)) return false;
            if (/<[^>]*>|script|javascript/i.test(trimmed)) return false;
            return true;
          }, {
            message: t('errors.midNameInvalid'),
          }),
        lastName: z.string()
          .min(1, { message: t('errors.lastNameRequired') })
          .refine((val) => {
            const trimmed = val.trim();
            if (trimmed.length === 0) return false;
            if (!/[a-zA-Zа-яА-Яéñüóíáúèàùëïöäüÿçñ]/i.test(trimmed)) return false;
            if (/[0-9@#$%^&*()_+=<>{}[\]|\\/`~]/.test(trimmed)) return false;
            if (/<[^>]*>|script|javascript/i.test(trimmed)) return false;
            return true;
          }, {
            message: t('errors.lastNameInvalid'),
          }),
        gender: z.enum(['male', 'female'], {
          message: t('errors.genderRequired'),
        }),

        dateOfBirth: z.object({
          month: z.string().min(1, { message: t('errors.monthRequired') }),
          day: z.string().min(1, { message: t('errors.dayRequired') }),
          year: z.string().min(1, { message: t('errors.yearRequired') }),
        }),
        height: z
          .object({
            ft: z.preprocess(
              (val) => (val === '' ? undefined : Number(val)),
              z
                .number({ invalid_type_error: t('errors.heightRequired') })
                .min(1, { message: t('errors.heightTooHigh') })
                .max(7, { message: t('errors.heightTooHigh') })
            ),
            in: z.preprocess(
              (val) => (val === '' ? undefined : Number(val)),
              z
                .number({ invalid_type_error: t('errors.heightRequired') })
                .min(0, { message: t('errors.inchesTooHigh') })
                .max(11, { message: t('errors.inchesTooHigh') })
            ),
          })
          .superRefine(({ ft, in: inches }, ctx) => {
            if (ft === 0 && inches === 0) {
              ctx.addIssue({
                path: ['ft'],
                code: z.ZodIssueCode.custom,
                message: t('errors.zeroHeight'),
              });
            }
          }),

        weight: z
          .preprocess(
            (val) => (val === '' ? undefined : Number(val)),
            z
              .number({ invalid_type_error: t('errors.weightRequired') })
              .min(7, { message: t('errors.weightTooLow') })
              .max(800, { message: t('errors.weightTooHigh') })
          )
          .superRefine((val, ctx) => {
            if (val === undefined) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('errors.weightRequired'),
              });
            }
          }),
        nicotine: z.boolean(),
        ssn: z.string().optional().default(''),
      })
  ),

  // Main applicant's phone — stored only (not part of any form's validation).
  phone: z.string().optional(),

  healthQuestions: z
    .object({
      initialQuestions: z
        .object({
          treatment: z
            .boolean({
              required_error: STEP_PHQ_1_ERRORS.treatment,
            })
            .refine((val) => val !== undefined, {
              message: STEP_PHQ_1_ERRORS.treatment,
            }),
          invalid: z
            .boolean({
              required_error: STEP_PHQ_1_ERRORS.invalid,
            })
            .refine((val) => val !== undefined, {
              message: STEP_PHQ_1_ERRORS.invalid,
            }),
          pregnancy: z
            .boolean({
              required_error: STEP_PHQ_1_ERRORS.pregnancy,
            })
            .refine((val) => val !== undefined, {
              message: STEP_PHQ_1_ERRORS.pregnancy,
            }),
        })
        .superRefine((data, ctx) => {
          (['treatment', 'invalid', 'pregnancy'] as const).forEach((field) => {
            if (data[field] === undefined) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: STEP_PHQ_1_ERRORS[field],
              });
            }
          });
        }),

      takingMedicationsNow: z.object({
        value: z.boolean({
          required_error: t('errors.selectAnyValue'),
        }),
        // medications: medicationSchema
      }),

      surgeriesDiabetesHeartMental: z.object({
        conditions: z
          .array(z.object({ value: z.string() }))
          .min(1, { message: PLEASE_SELECT_LEAST }),
        details: z.record(z.string()).optional(),
      }),
      medDetails: z
        .object({
          medications: z
            .array(z.object({ value: z.string(), details: z.string() }))
            .min(1, { message: PLEASE_SELECT_LEAST })
            .optional(),
          details: z.record(z.string()).optional(),
        })
        .optional(),
      otherHealthConditions: z
        .object({
          conditions: z
            .array(z.object({ value: z.string() }))
            .min(1, { message: PLEASE_SELECT_LEAST })
            .optional(),
          details: z.record(z.string()).optional(),
          diabetesType: z.string().optional(),
        })
        // .superRefine((data, ctx) => {
        //   if (data.conditions && data.details) {
        //     data.conditions.forEach((cond) => {
        //       const val = data.details?.[cond.value];
        //       if (!val || val.trim() === '') {
        //         ctx.addIssue({
        //           path: ['details', cond.value],
        //           code: z.ZodIssueCode.custom,
        //           message: 'Please provide details',
        //         });
        //       }
        //     });
        //   }
        // })
        .optional(),
      finalHealthConditions: z
        .object({
          value: z
            .boolean({ required_error: t('errors.yesNoRequired') })
            .optional(),
        })
        .optional(),
      // otherMedicalConcerns: z
      //   .object({
      //     hasOtherConcerns: z
      //       .boolean({ required_error: 'Please answer this question' })
      //       .optional(),
      //     additionalDetails: z
      //       .string()
      //       .max(500, { message: 'Too much text' })
      //       .optional(),
      //   })
      //   .optional(),

      coverageStartDate: z
        .object({
          effectiveDate: z
            .string({ required_error: t('errors.effectiveDateRequired') })
            .optional(),
        })
        .optional(),
      privacyDisclosure: z.object({
        disclaimer: z.boolean({
          required_error: t('errors.disclaimerRequired'),
        }),
        accepted: z.boolean({
          required_error: t('errors.privacyDisclosureRequired'),
        }),
        signature: z.string().refine((v) => v.trim().length >= 3, {
          message: 'Minimal length is 3 characters',
        }),
      }),
    })
    .optional(),

  medicalTags: z.array(z.string()).optional(),

  medicalPlanRejected: z.boolean().optional(),
  medicalPlan: z
    .string()
    .nullish()
    // .min(1, {
    //   message:
    //     'If you do not wish to select an Medical plan please make sure to click the ’I wish to waive” check box.',
    // })
    .optional(),
  medicalPlanPrice: z.number().optional(),
  medicalPlanTier: z.string().optional(),
  medicalPlanCoverageAmount: z.number().optional(),
  medicalPlansToCompare: z.array(z.string()).optional(),

  dentalPlanPrice: z.number().optional(),
  planTags: z.record(z.string(), z.array(z.string())).optional(), // Dynamic tags for each plan UID
  dentalAndVisionTags: z.array(z.string()).optional(), // Keep for backward compatibility
  dentalPlan: z
    .string()
    .nullish()
    // .min(1, {
    //   message:
    //     'If you do not wish to select an Dental plan please make sure to click the ’I wish to waive” check box.',
    // })
    .optional(),
  dentalPlanTier: z.string().optional(),
  dentalPlanCoverageAmount: z.number().optional(),
  dentalPlansToCompare: z.array(z.string()).optional(),

  visionPlan: z.string().optional(),

  marketplaceTags: z.array(z.string()).optional(),

  identityProtectionPlan: z
    .string()
    // .min(1, {
    //   message:
    //     'If you do not wish to select an Identity plan please make sure to click the ’I wish to waive” check box.',
    // })
    .optional(),
  identityProtectionPlansToCompare: z.array(z.string()).optional(),
  identityProtectionPlanPrice: z.number().optional(),
  legalPlan: z
    .string()
    // .min(1, {
    //   message:
    //     'If you do not wish to select an Legal plan please make sure to click the ’I wish to waive” check box.',
    // })
    .optional(),
  legalPlansToCompare: z.array(z.string()).optional(),
  legalPlanPrice: z.number().optional(),
  telehealthPlan: z
    .string()
    // .min(1, {
    //   message:
    //     'If you do not wish to select an Telehealth plan please make sure to click the ’I wish to waive” check box.',
    // })
    .optional(),
  totalPlanPrice: z.number().optional(),
  telehealthPlansToCompare: z.array(z.string()).optional(),
  
  criticalillnessPlan: z
    .string()
    .optional(),
  criticalillnessPlanPrice: z.number().optional(),
  criticalillnessPlanTier: z.string().optional(),
  criticalillnessPlanCoverageAmount: z.number().optional(),
  criticalillnessPlanDependentBenefit: z.number().optional(),
  criticalillnessPlansToCompare: z.array(z.string()).optional(),
  
  // Address
  address: z.object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipcode: z.string().optional(),
    country: z.string().optional(),
  }).optional(),
});

export const dependentSchema = onboardingSchema.pick({
  applicants: true,
  householdIncome: true,
});

export const healthQuestionsSchema = onboardingSchema.pick({
  healthQuestions: true,
});

export const healthQuestionsSchemaWithConditional =
  healthQuestionsSchema.superRefine((data, ctx: RefinementCtx) => {
    // const initialQ1 = data.healthQuestions?.initialQuestions?.treatment;
    // const conditions = data.healthQuestions?.medDetails?.conditions;
    // if ((initialQ1 === true || initialQ1 === false) && (!conditions || conditions.length === 0)) {
    //   ctx.addIssue({
    //     path: ['healthQuestions', 'medDetails', 'conditions'],
    //     code: z.ZodIssueCode.custom,
    //     message: PLEASE_SELECT_LEAST,
    //   });
    // }
    // const initialQ1 = data?.healthQuestions?.initialQuestions?.treatment;
    // const currentStepIndex = HEALTH_QUESTIONS.findIndex((q) => q.id === stepId);
    // const medDetailsIndex = HEALTH_QUESTIONS.findIndex((q) => q.id === 'medDetails');
    // const privacyDisclosureIndex = HEALTH_QUESTIONS.findIndex((q) => q.id === 'privacyDisclosure');
    // const coverageStartDateIndex = HEALTH_QUESTIONS.findIndex((q) => q.id === 'coverageStartDate');
    // if (
    //   currentStepIndex >= coverageStartDateIndex &&
    //   currentStepIndex <= medDetailsIndex &&
    //   initialQ1 === true &&
    //   (!data?.healthQuestions?.medDetails?.conditions ||
    //     data?.healthQuestions?.medDetails?.conditions.length === 0)
    // ) {
    //   ctx.addIssue({
    //     path: ['healthQuestions', 'medDetails', 'conditions'],
    //     code: z.ZodIssueCode.custom,
    //     message: PLEASE_SELECT_LEAST,
    //   });
    // }
    // if (currentStepIndex >= privacyDisclosureIndex) {
    //   if (!data?.healthQuestions?.privacyDisclosure?.accepted) {
    //     ctx.addIssue({
    //       path: ['healthQuestions', 'privacyDisclosure', 'accepted'],
    //       code: z.ZodIssueCode.custom,
    //       message: 'Please acknowledge the privacy disclosure.'
    //     });
    //   }
    //   if (!data?.healthQuestions?.privacyDisclosure?.signature) {
    //     ctx.addIssue({
    //       path: ['healthQuestions', 'privacyDisclosure', 'signature'],
    //       code: z.ZodIssueCode.custom,
    //       message: 'Please provide a signature.'
    //     });
    //   }
    // }
  });

export const medicalPlansSchema = onboardingSchema.pick({
  medicalTags: true,

  medicalPlan: true,
  medicalPlansToCompare: true,
});

export const dentalAndVisionPlansSchema = onboardingSchema.pick({
  planTags: true,
  dentalAndVisionTags: true, // Keep for backward compatibility

  dentalPlan: true,
  dentalPlansToCompare: true,
});

export const marketplacePlansSchema = onboardingSchema.pick({
  marketplaceTags: true,

  medicalPlan: true,
  dentalPlan: true,

  identityProtectionPlan: true,
  identityProtectionPlansToCompare: true,

  legalPlan: true,
  legalPlansToCompare: true,

  telehealthPlan: true,
  telehealthPlansToCompare: true,
  
  // Critical Illness Plan fields
  criticalillnessPlan: true,
  criticalillnessPlanPrice: true,
  criticalillnessPlanTier: true,
  criticalillnessPlanCoverageAmount: true,
  criticalillnessPlanDependentBenefit: true,
  criticalillnessPlansToCompare: true,
});

export type OnboardingSchema = z.infer<typeof onboardingSchema>;
export type DependentSchema = z.infer<typeof dependentSchema>;
export type DependentItemSchema = z.infer<
  typeof onboardingSchema
>['applicants'][number];

export type DependentItemFormType = Omit<
  DependentItemSchema,
  'gender' | 'height' | 'weight'
> & {
  gender: 'male' | 'female' | undefined;
  height: {
    ft: number | undefined;
    in: number | undefined;
  };
  weight: number | undefined;
};
export type MedicalPlansSchema = z.infer<typeof medicalPlansSchema>;
export type DentalAndVisionPlansSchema = z.infer<
  typeof dentalAndVisionPlansSchema
>;
export type SummaryPlansSchema = z.infer<typeof marketplacePlansSchema>;
export type HealthQuestionsSchema = z.infer<typeof healthQuestionsSchema>;
